import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Box, Typography, CircularProgress, Button, Alert } from '@mui/material';
import keycloakAuth from '../services/KeycloakAuth';

// Roles that grant access to the admin portal at all (per-route roles narrow further)
const ADMIN_PORTAL_ROLES = ['ADMIN', 'OPERATIONS', 'FINANCE', 'COMPLIANCE'];

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
  const { isAuthenticated, loading, hasRole, logout, getRoles } = useAuth();

  // Not authenticated - send to the Keycloak-hosted login page. init() with
  // login-required normally does this already; this covers the fallback case
  // (e.g. init failure) so the user is never stuck on a blank app. The
  // sessionStorage guard prevents a redirect loop when sign-in itself fails:
  // we auto-redirect only once, then surface an error with a manual retry.
  const alreadyTriedLogin = sessionStorage.getItem('kc-login-attempted') === '1';
  useEffect(() => {
    if (!loading) {
      if (isAuthenticated) {
        sessionStorage.removeItem('kc-login-attempted');
      } else if (!alreadyTriedLogin) {
        sessionStorage.setItem('kc-login-attempted', '1');
        keycloakAuth.login();
      }
    }
  }, [loading, isAuthenticated, alreadyTriedLogin]);

  // Still loading authentication state, or the login redirect is in flight
  if (loading || (!isAuthenticated && !alreadyTriedLogin)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  // Sign-in failed (we already round-tripped to Keycloak once): show a retry
  // instead of looping.
  if (!isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" p={3}>
        <Box maxWidth={450} width="100%">
          <Alert severity="error" sx={{ mb: 2 }}>
            Sign-in could not be completed. Please try again.
          </Alert>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              sessionStorage.removeItem('kc-login-attempted');
              keycloakAuth.login();
            }}
          >
            Retry sign in
          </Button>
        </Box>
      </Box>
    );
  }

  // Authenticated but no admin-portal role at all (e.g. a merchant/retailer
  // user): deny access to the whole portal, offer sign-out.
  const hasPortalAccess = getRoles().some((role) => ADMIN_PORTAL_ROLES.includes(role));
  if (!hasPortalAccess) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" p={3}>
        <Box maxWidth={450} width="100%">
          <Alert severity="error" sx={{ mb: 2 }}>
            Access denied. Your account does not have admin portal access -
            please use the merchant or retailer portal.
          </Alert>
          <Button fullWidth variant="outlined" onClick={logout}>
            Sign out
          </Button>
        </Box>
      </Box>
    );
  }

  // Check per-route roles if specified
  if (roles && roles.length > 0) {
    const hasRequiredRole = roles.some((role) => hasRole(role));

    if (!hasRequiredRole) {
      return (
        <Box p={3}>
          <Typography color="error">
            You don't have permission to access this page.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Required roles: {roles.join(', ')}
          </Typography>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
